import { Body, Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { CancelIcon } from '@depop/web-ui-kit/Icons/CancelIcon';
import { Modal } from '@depop/web-ui-kit/Modal';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { UpfrontShippingBanner } from '../UpfrontShippingBanner';

import styles from './styles.module.css';

import { Price } from '@/components/Price';
import { ON_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { SupportedLocation } from '@/modules/location/constants';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { type ProductPrice } from '@/modules/product/types';

const FEE_BENEFITS_ITEM_KEYS = [
  'MarketplaceFeeBenefitsItemDepopProtection',
  'MarketplaceFeeBenefitsItemCustomerSupport',
  'MarketplaceFeeBenefitsItemNewFeatures',
] as const;

const AU_FEE_BENEFITS_ITEM_KEYS = [
  'MarketplaceFeeBenefitsAuItemCustomerSupport',
  'MarketplaceFeeBenefitsAuItemSecureTransactions',
  'MarketplaceFeeBenefitsAuItemFraudPrevention',
  'MarketplaceFeeBenefitsAuItemAccountSecurity',
] as const;

type Props = {
  isOpen: boolean;
  setOpen: () => void;
  price: ProductPrice;
};

export function ProductPriceBreakdownModal({ isOpen, setOpen, price }: Props) {
  const t = useTranslations('common');
  const { web_upfront_shipping_banner } = useExperiments([
    'web_upfront_shipping_banner',
  ]);
  const shippingBannerDisplayed = web_upfront_shipping_banner === ON_IDENTIFIER;

  const { location } = useCurrentLocation();
  const showAuFeeCopy = location === SupportedLocation.AU;
  const feeBenefitsIntroKey = showAuFeeCopy
    ? 'MarketplaceFeeBenefitsAuIntro'
    : 'MarketplaceFeeBenefitsIntro';
  const feeBenefitsItemKeys = showAuFeeCopy
    ? AU_FEE_BENEFITS_ITEM_KEYS
    : FEE_BENEFITS_ITEM_KEYS;

  const isUpfrontShipping = price.inclShippingFee;

  const isFreeShipping = price.shippingCost === '0.00';

  return (
    <Modal
      wide
      isOpen={isOpen}
      setOpen={setOpen}
      hideCloseButton
      labelledBy="price-breakdown-modal-title"
      customOverrides={{ floatingHeader: true }}
      content={() => (
        <div className={styles.modalContent}>
          {shippingBannerDisplayed && (
            <UpfrontShippingBanner className={styles.shippingBannerSpacing} />
          )}
          <div className={styles.headerContainer}>
            <Heading variant="lg" id="price-breakdown-modal-title">
              {t('Total')}
            </Heading>
            <button className={styles.closeButton} onClick={setOpen}>
              <CancelIcon title="Close" aria-label="Close" />
            </button>
          </div>
          <div className={styles.row}>
            <Body variant="sm-regular">{t('ItemPrice')}</Body>
            <Price
              ariaDescription={t('ItemPrice')}
              className={styles.price}
              currencyCode={price.currencyName}
              price={price.baseCurrentPrice}
            />
          </div>
          {price.inclTax &&
            price.currentSalesTax &&
            Number(price.currentSalesTax) > 0 && (
              <div className={styles.row}>
                <Body variant="sm-regular">{t('SalesTax')}</Body>
                <Price
                  className={styles.price}
                  ariaDescription={t('SalesTax')}
                  price={price.currentSalesTax}
                  currencyCode={price.currencyName}
                />
              </div>
            )}
          {price.inclShippingFee && price.shippingCost && (
            <div className={styles.row}>
              <Body variant="sm-regular">{t('Shipping')}</Body>
              {isFreeShipping ? (
                <Body
                  variant="sm-regular"
                  className={styles.freeShippingText}
                  aria-label={t('Shipping')}
                >
                  {t('Free')}
                </Body>
              ) : (
                <Price
                  className={styles.price}
                  ariaDescription={t('Shipping')}
                  price={price.shippingCost}
                  currencyCode={price.currencyName}
                />
              )}
            </div>
          )}
          {price.inclBuyerFee &&
            price.currentPriceFees &&
            Number(price.currentPriceFees) > 0 && (
              <div className={styles.row}>
                <div>
                  <Body variant="sm-regular">{t('MarketplaceFee')}</Body>
                  <Caption variant="lg-regular">
                    {t(feeBenefitsIntroKey)}
                  </Caption>
                  <ul className={styles.marketplaceFeesBenefitsList}>
                    {feeBenefitsItemKeys.map((itemKey) => (
                      <li key={itemKey}>
                        <Caption variant="lg-regular">{t(itemKey)}</Caption>
                      </li>
                    ))}
                  </ul>
                </div>
                <Price
                  className={styles.price}
                  ariaDescription={t('MarketplaceFee')}
                  price={price.currentPriceFees}
                  currencyCode={price.currencyName}
                />
              </div>
            )}
          <div className={styles.row}>
            <Body variant="sm-bold">{t('Total')}</Body>
            <Price
              ariaDescription={t('Total')}
              className={clsx(styles.price, styles.totalPrice)}
              price={price.totalCurrentPrice}
              currencyCode={price.currencyName}
            />
          </div>
          {!isUpfrontShipping && !shippingBannerDisplayed && (
            <Caption variant="lg-regular" className={styles.footerText}>
              {t('ShippingIsCalculatedAtCheckout')}
            </Caption>
          )}
        </div>
      )}
    />
  );
}
